import React, { useState, useEffect } from "react";
import { useCustomer } from "../../context/CustomerContext";
import axiosInstance from "../../api/axiosInstance";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import {
  FiCalendar,
  FiEdit3,
  FiLock,
  FiMail,
  FiPhone,
  FiSave,
  FiUser,
  FiX,
} from "react-icons/fi";
import styles from "./ProfileSection.module.css";

const ProfileSection = () => {
  const { customer, setCustomer } = useCustomer();

  const hasPassword =
    customer?.hasPassword ?? (customer?.authProvider !== "google");
  const isGoogleWithoutPassword =
    customer?.authProvider === "google" && !hasPassword;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    dateOfBirth: "",
    email: "",
  });
  const [originalForm, setOriginalForm] = useState(form);

  const [isEditing, setIsEditing] = useState(false);

  // Для зміни пароля
  const [changePassword, setChangePassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordMsg, setPasswordMsg] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Стейти для показу/приховування паролів
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const customerName = [form.firstName, form.lastName].filter(Boolean).join(" ");

  // При зміні customer оновлюємо локальний state профілю
  useEffect(() => {
    if (customer) {
      const customerData = {
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        phone: customer.phone || "",
        email: customer.email || "",
        dateOfBirth: customer.dateOfBirth
          ? new Date(customer.dateOfBirth).toISOString().split("T")[0]
          : "",
      };
      setForm(customerData);
      setOriginalForm(customerData);
    }
  }, [customer]);

  const handleProfileChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(""); // очищаємо помилку при кожній зміні
    setSuccess(""); // і повідомлення про успіх
  };

  // Оновлення профілю: редагуються ПІБ, телефон, email та дата народження.
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    // Перевірка: якщо немає змін — не сабмітити
    if (JSON.stringify(form) === JSON.stringify(originalForm)) {
      setError("Ви не змінили жодного поля.");
      return;
    }
    try {
      const res = await axiosInstance.put("/customers/me", {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        dateOfBirth: form.dateOfBirth,
        email: form.email,
      });
      setCustomer(res.data.customer);
      setSuccess("Профіль оновлено!");
      setOriginalForm(form);
      setIsEditing(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Помилка оновлення профілю");
    }
  };

  const handleCancelEdit = () => {
    setForm(originalForm);
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  // Зміна пароля
  const handlePasswordChange = (e) => {
    setChangePassword({ ...changePassword, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMsg("");

    if (changePassword.newPassword !== changePassword.confirmPassword) {
      setPasswordMsg("Новий пароль і підтвердження не співпадають!");
      return;
    }

    if (!isGoogleWithoutPassword && !changePassword.oldPassword) {
      setPasswordMsg("Введіть старий пароль");
      return;
    }

    if (changePassword.newPassword.length < 6) {
      setPasswordMsg("Новий пароль має містити щонайменше 6 символів");
      return;
    }

    try {
      const endpoint = isGoogleWithoutPassword
        ? "/customers/me/password/set"
        : "/customers/me/password";
      const payload = isGoogleWithoutPassword
        ? { newPassword: changePassword.newPassword }
        : {
            currentPassword: changePassword.oldPassword,
            newPassword: changePassword.newPassword,
          };
      const res = await axiosInstance.put(endpoint, payload);

      // 🔹 зберігаємо новий токен у localStorage
      if (res.data?.token) {
        localStorage.setItem("token", res.data.token);
      }

      if (res.data?.customer) {
        setCustomer((prev) => ({
          ...prev,
          ...res.data.customer,
        }));
      } else if (isGoogleWithoutPassword) {
        setCustomer((prev) => ({ ...prev, hasPassword: true }));
      }

      setPasswordMsg(
        isGoogleWithoutPassword
          ? "Пароль успішно встановлено!"
          : "Пароль успішно змінено!"
      );
      setChangePassword({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPasswordMsg(
        err?.response?.data?.message || "Помилка при зміні пароля"
      );
    }
  };

  return (
    <div className={styles.profileWrapper}>
      <div className={styles.sectionHero}>
        <div>
          <span className={styles.eyebrow}>Профіль</span>
          <h1>Особистий кабінет</h1>
          <p>
            Керуйте контактами, персональними даними та безпекою облікового запису.
          </p>
        </div>
        <div className={styles.profileSummary}>
          <div className={styles.summaryAvatar} aria-hidden="true">
            {(form.firstName?.[0] || "M") + (form.lastName?.[0] || "Z")}
          </div>
          <div>
            <strong>{customerName || "Покупець MyZooShop"}</strong>
            <span>{form.email || "Email не вказано"}</span>
          </div>
        </div>
      </div>

      <form
        id="profileForm"
        className={`${styles.profileForm} ${styles.crmCard}`}
        onSubmit={isEditing ? handleProfileSubmit : (e) => e.preventDefault()}
        noValidate
        autoComplete="off"
      >
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <FiUser />
          </div>
          <div>
            <h2>Дані покупця</h2>
            <p>Основні дані, які використовуються для замовлень і доставки.</p>
          </div>
        </div>

        <div className={styles.twoColumns}>
          <div className={styles.inputGroup}>
            <label htmlFor="firstName">
              <FiUser /> Імʼя
            </label>
            <input
              name="firstName"
              id="firstName"
              type="text"
              value={form.firstName}
              onChange={handleProfileChange}
              required
              autoComplete="given-name"
              disabled={!isEditing}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="lastName">Прізвище</label>
            <input
              name="lastName"
              id="lastName"
              type="text"
              value={form.lastName}
              onChange={handleProfileChange}
              required
              autoComplete="family-name"
              disabled={!isEditing}
            />
          </div>
        </div>
        <div className={styles.twoColumns}>
          <div className={styles.inputGroup}>
            <label htmlFor="phone">
              <FiPhone /> Контактний телефон
            </label>
            <input
              name="phone"
              id="phone"
              type="tel"
              value={form.phone}
              onChange={handleProfileChange}
              placeholder="+38 (0__) ___ ___ ______"
              required
              autoComplete="tel"
              disabled={!isEditing}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="email">
              <FiMail /> Email
            </label>
            <input
              name="email"
              id="email"
              type="email"
              value={form.email}
              onChange={handleProfileChange}
              required
              autoComplete="email"
              disabled={!isEditing} // ← зроби як для інших полів
            />
          </div>
        </div>
        <div className={styles.twoColumns}>
          <div className={styles.inputGroup}>
            <label htmlFor="dateOfBirth">
              <FiCalendar /> Дата народження
            </label>
            <input
              name="dateOfBirth"
              id="dateOfBirth"
              type="date"
              value={form.dateOfBirth}
              onChange={handleProfileChange}
              required
              autoComplete="bday"
              disabled={!isEditing}
            />
          </div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        <div className={styles.buttonRow}>
          {!isEditing ? (
            <button
              type="button"
              className={styles.saveButton}
              onClick={() => setIsEditing(true)}
            >
              <FiEdit3 />
              Редагувати
            </button>
          ) : (
            <>
              <button
                type="submit"
                className={`${styles.saveButton} ${styles.greenButton}`}
                disabled={JSON.stringify(form) === JSON.stringify(originalForm)}
              >
                <FiSave />
                Зберегти зміни
              </button>
              <button
                type="button"
                className={`${styles.saveButton} ${styles.redButton}`}
                onClick={handleCancelEdit}
              >
                <FiX />
                Скасувати
              </button>
            </>
          )}
        </div>
      </form>

      <form
        id={isGoogleWithoutPassword ? "setPasswordForm" : "passwordForm"}
        className={`${styles.passwordForm} ${styles.crmCard}`}
        onSubmit={handlePasswordSubmit}
        noValidate
        autoComplete="off"
      >
        <div className={styles.cardHeader}>
          <div className={`${styles.cardIcon} ${styles.cardIconBlue}`}>
            <FiLock />
          </div>
          <div>
            <h2>
              {isGoogleWithoutPassword ? "Створити пароль" : "Змінити пароль"}
            </h2>
            <p>Оновіть пароль для безпечного входу в особистий кабінет.</p>
          </div>
        </div>
        {isGoogleWithoutPassword && (
          <p className={styles.passwordHint}>
            Для Google-акаунта можна встановити локальний пароль і надалі
            входити через email та пароль.
          </p>
        )}
        {!isGoogleWithoutPassword && (
          <div className={styles.inputGroup}>
            <label htmlFor="oldPassword">Старий пароль</label>
            <div className={styles.inputWrapper}>
              <input
                name="oldPassword"
                id="oldPassword"
                type={showOld ? "text" : "password"}
                value={changePassword.oldPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="current-password"
                className={styles.input}
              />
              <button
                type="button"
                className={styles.eyeButton}
                tabIndex={-1}
                onClick={() => setShowOld((v) => !v)}
                aria-label={showOld ? "Приховати пароль" : "Показати пароль"}
              >
                {showOld ? <IoEyeOutline /> : <IoEyeOffOutline />}
              </button>
            </div>
          </div>
        )}
        <div className={styles.inputGroup}>
          <label htmlFor="newPassword">Новий пароль</label>
          <div className={styles.inputWrapper}>
            <input
              name="newPassword"
              id="newPassword"
              type={showNew ? "text" : "password"}
              value={changePassword.newPassword}
              onChange={handlePasswordChange}
              required
              autoComplete="new-password"
              className={styles.input}
            />
            <button
              type="button"
              className={styles.eyeButton}
              tabIndex={-1}
              onClick={() => setShowNew((v) => !v)}
              aria-label={showNew ? "Приховати пароль" : "Показати пароль"}
            >
              {showNew ? <IoEyeOutline /> : <IoEyeOffOutline />}
            </button>
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="confirmPassword">Підтвердіть новий пароль</label>
          <div className={styles.inputWrapper}>
            <input
              name="confirmPassword"
              id="confirmPassword"
              type={showConfirm ? "text" : "password"}
              value={changePassword.confirmPassword}
              onChange={handlePasswordChange}
              required
              autoComplete="new-password"
              className={styles.input}
            />
            <button
              type="button"
              className={styles.eyeButton}
              tabIndex={-1}
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? "Приховати пароль" : "Показати пароль"}
            >
              {showConfirm ? <IoEyeOutline /> : <IoEyeOffOutline />}
            </button>
          </div>
        </div>
        {passwordMsg && (
          <div
            className={
              passwordMsg.includes("успішно") ? styles.success : styles.error
            }
          >
            {passwordMsg}
          </div>
        )}
        <div className={styles.buttonRow}>
          <button
            type="submit"
            className={styles.saveButton}
            form={isGoogleWithoutPassword ? "setPasswordForm" : "passwordForm"}
          >
            {isGoogleWithoutPassword ? "Створити пароль" : "Змінити пароль"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSection;
